"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  TURKISH_CASE_STRINGS,
  TurkishCaseToggle,
  useTurkishCase,
} from "@/components/tools/turkish-case-toggle";
import { writeToClipboard } from "@/lib/clipboard";
import { foldLower, foldUpper } from "@/lib/turkish-case";

const RE_STANDALONE_I = /\bi\b/g;

/*
 * The two capitalisation anchors by Unicode property, not by `\w`.
 *
 * `\w` is `[A-Za-z0-9_]` in JavaScript even under the `u` flag, so both of these missed
 * every sentence and every line that did not open on an ASCII letter. The tool's whole
 * output is `foldLower(text)` with these two put back up, which means for Russian,
 * Arabic, Hindi, Korean, Vietnamese with a diacritic on the first letter, and German or
 * Turkish opening on `ü`/`ö`/`ş`/`İ`, it lower-cased the reader's text and then
 * capitalised nothing — strictly destructive. "über allen gipfeln ist ruh." stayed
 * lower-case; "đây là câu." stayed lower-case; "Десять способов" came back as "десять
 * способов".
 *
 * Each of these matches ONE character and folds it up, so neither needs `\p{M}`: there
 * is no word here to hold together, and a combining mark cannot begin a sentence.
 *
 * `RE_STANDALONE_I` and the acronym list below stay ASCII deliberately — a standalone
 * "I" and `NASA`/`PDF` are English orthography, the same call `MINOR_WORDS` makes in
 * the sibling converters.
 */
const RE_AFTER_NEWLINE = /(^\s*[\p{L}\p{N}_]|\n\s*[\p{L}\p{N}_])/gu;
const RE_FIRST_CHAR = /^\s*[\p{L}\p{N}_]/u;

const RE_SENTENCE_SPLIT = /[.!?]+/;

/*
 * Sentence starts inside a line: a full stop, question mark or exclamation mark (with any
 * closing quote or bracket), whitespace, then a letter. Group 1 is everything up to the
 * letter, group 2 the letter that gets the capital.
 */
const RE_MID_LINE_START = /([.!?]+["'”’)\]]*[^\S\n]+)(\p{L})/gu;
const RE_WORD_BEFORE_STOP = /([\p{L}.]+)\.$/u;
const RE_ENGLISH_WORD = /\b[A-Za-z]+\b/g;
const RE_CAPS_TOKEN = /\b[A-Z]{2,}\b/g;
const RE_ANY_LOWERCASE = /\p{Ll}/u;

/*
 * A full stop after these does not end a sentence, so the next word keeps its case. A
 * lone initial ("J. Smith") is handled in `endsAbbreviation`.
 */
const ABBREVIATIONS = new Set([
  "e.g",
  "i.e",
  "eg",
  "ie",
  "vs",
  "cf",
  "mr",
  "mrs",
  "ms",
  "dr",
  "prof",
  "fig",
  "al",
  "approx",
  "inc",
  "ltd",
]);

/**
 * Acronyms that are not also ordinary words. They come back in capitals wherever they
 * appear, whatever case the reader typed them in.
 */
const SAFE_ACRONYMS = [
  "USA",
  "UK",
  "NASA",
  "HTML",
  "CSS",
  "API",
  "URL",
  "PDF",
  "PhD",
  "CEO",
  "FBI",
  "CIA",
  "EU",
  "GDP",
  "DNA",
  "RNA",
  "AI",
  "HR",
  "PR",
  "SEO",
  "FAQ",
  "DIY",
  "ASAP",
  "FYI",
  "RSVP",
  "ATM",
  "GPS",
  "USB",
  "GPA",
  "GRE",
  "LSAT",
  "MCAT",
  "APA",
  "MLA",
  "IEEE",
] as const;

/**
 * Acronyms that are also ordinary words ("it", "act", "sat", "stem", and "un" in French
 * and Spanish). They are only restored when the reader typed them in capitals inside text
 * that is not all capitals; in an ALL CAPS paragraph there is no way to tell "IT" the
 * department from "IT" the pronoun, so the pronoun wins.
 */
const WORD_ACRONYMS = ["IT", "ACT", "SAT", "STEM", "UN"] as const;

const ACRONYM_BY_LOWER = new Map<string, string>(
  [...SAFE_ACRONYMS, ...WORD_ACRONYMS].map((a) => [a.toLowerCase(), a])
);
const WORD_ACRONYM_KEYS = new Set<string>(
  WORD_ACRONYMS.map((a) => a.toLowerCase())
);

/** English strings. A translated content file overrides these through `ui`. */
export const SENTENCE_CASE_CONVERTER_STRINGS = {
  ...TURKISH_CASE_STRINGS,
  inputText: "Input Text",
  placeholder:
    "Type or paste your text here (e.g., ALL CAPS TEXT or Title Case Text)...",
  sentenceCaseOutput: "Sentence Case Output",
  copied: "Copied!",
  copyToClipboard: "Copy to Clipboard",
  characters: "Characters",
  sentences: "Sentences",
  charactersChanged: "Characters Changed",
} as const;

function endsAbbreviation(before: string): boolean {
  const match = RE_WORD_BEFORE_STOP.exec(before);
  if (!match) {
    return false;
  }
  const word = match[1].toLowerCase();
  return word.length === 1 || ABBREVIATIONS.has(word);
}

/** Capitalise the first letter of every sentence that starts mid-line. */
function capitaliseMidLine(text: string, turkish: boolean): string {
  return text.replace(
    RE_MID_LINE_START,
    (match: string, lead: string, letter: string, offset: number) => {
      const before = text.slice(0, offset + lead.trimEnd().length);
      if (lead.startsWith(".") && endsAbbreviation(before)) {
        return match;
      }
      return lead + foldUpper(letter, turkish);
    }
  );
}

/** Lower-cased forms of the all-caps tokens the reader typed. */
function typedInCapitals(text: string): Set<string> {
  const found = new Set<string>();
  for (const token of text.match(RE_CAPS_TOKEN) ?? []) {
    found.add(token.toLowerCase());
  }
  return found;
}

/** Put known acronyms back; see SAFE_ACRONYMS and WORD_ACRONYMS for the rules. */
function restoreAcronyms(
  result: string,
  original: string,
  allCaps: boolean
): string {
  const capitals = typedInCapitals(original);
  return result.replace(RE_ENGLISH_WORD, (word: string) => {
    const key = word.toLowerCase();
    const acronym = ACRONYM_BY_LOWER.get(key);
    if (!acronym) {
      return word;
    }
    if (WORD_ACRONYM_KEYS.has(key) && (allCaps || !capitals.has(key))) {
      return word;
    }
    return acronym;
  });
}

export function toSentenceCase(text: string, turkish: boolean): string {
  const allCaps = !RE_ANY_LOWERCASE.test(text);

  // First, lowercase everything
  let result = foldLower(text, turkish);

  // Capitalize first character of the string
  result = result.replace(RE_FIRST_CHAR, (match) => foldUpper(match, turkish));

  // Capitalize the first letter after sentence-ending punctuation on the same line
  result = capitaliseMidLine(result, turkish);

  // Capitalize after newlines
  result = result.replace(RE_AFTER_NEWLINE, (match) =>
    foldUpper(match, turkish)
  );

  // Fix standalone "I"
  result = result.replace(RE_STANDALONE_I, "I");

  return restoreAcronyms(result, text, allCaps);
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function SentenceCaseConverterWidget() {
  const strings = useToolUi(SENTENCE_CASE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [turkish, setTurkish] = useTurkishCase();

  const hasInput = input.trim().length > 0;
  const output = hasInput ? toSentenceCase(input, turkish) : "";
  const charCount = input.length;
  const sentenceCount = hasInput
    ? input.split(RE_SENTENCE_SPLIT).filter((s) => s.trim().length > 0).length
    : 0;
  const changedChars = hasInput
    ? [...input].filter((c, i) => c !== output[i]).length
    : 0;

  async function handleCopy() {
    if (output) {
      if (!(await writeToClipboard(output))) {
        return;
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="sc-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="sc-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.placeholder}
          value={input}
        />
        <TurkishCaseToggle
          checked={turkish}
          onChange={setTurkish}
          strings={strings}
        />
      </div>

      {/* Output */}
      {hasInput && (
        <>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label
                className="font-medium text-foreground text-sm"
                htmlFor="sc-output"
              >
                {strings.sentenceCaseOutput}
              </label>
              <button
                className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
                onClick={handleCopy}
                type="button"
              >
                {copied ? strings.copied : strings.copyToClipboard}
              </button>
            </div>
            <textarea
              className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-muted/30 p-4 text-foreground text-sm leading-relaxed"
              id="sc-output"
              readOnly
              value={output}
            />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <StatCard label={strings.characters} value={fmt(charCount)} />
            <StatCard label={strings.sentences} value={fmt(sentenceCount)} />
            <StatCard
              label={strings.charactersChanged}
              value={fmt(changedChars)}
            />
          </div>
        </>
      )}
    </>
  );
}
